import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Phone, Clock, User } from 'lucide-react';

export function CallbackCalendar({ callbacks, onCallbackClick }) {
    const [currentDate, setCurrentDate] = useState(moment());
    const [calendarDays, setCalendarDays] = useState([]);

    useEffect(() => {
        generateCalendar();
    }, [currentDate, callbacks]);

    const generateCalendar = () => {
        const startOfMonth = currentDate.clone().startOf('month');
        const endOfMonth = currentDate.clone().endOf('month');
        const startDate = startOfMonth.clone().startOf('week');
        const endDate = endOfMonth.clone().endOf('week');

        const days = [];
        let day = startDate.clone();

        while (day.isSameOrBefore(endDate, 'day')) {
            const dayCallbacks = callbacks.filter(cb => 
                moment(cb.callbackSchedule.scheduledFor).format('YYYY-MM-DD') === day.format('YYYY-MM-DD')
            );

            days.push({
                date: day.clone(),
                isCurrentMonth: day.month() === currentDate.month(),
                isToday: day.isSame(moment(), 'day'),
                callbacks: dayCallbacks
            });

            day.add(1, 'day');
        }

        setCalendarDays(days);
    };

    const goToPreviousMonth = () => {
        setCurrentDate(currentDate.clone().subtract(1, 'month'));
    };

    const goToNextMonth = () => {
        setCurrentDate(currentDate.clone().add(1, 'month'));
    };

    const goToToday = () => {
        setCurrentDate(moment());
    };

    const getCallbackColor = (callback) => {
        const scheduledTime = moment(callback.callbackSchedule.scheduledFor);
        const now = moment();

        if (callback.callbackSchedule.completed) {
            return 'bg-green-100 border-green-300 text-green-800';
        } else if (scheduledTime.isBefore(now)) {
            return 'bg-red-100 border-red-300 text-red-800';
        } else if (scheduledTime.diff(now, 'hours') < 2) {
            return 'bg-orange-100 border-orange-300 text-orange-800';
        } else {
            return 'bg-blue-100 border-blue-300 text-blue-800';
        }
    };

    return (
        <div className="bg-white rounded-lg shadow">
            {/* Calendar Header */}
            <div className="p-4 border-b flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <CalendarIcon className="h-5 w-5 text-gray-600" />
                    <h3 className="text-lg font-semibold text-gray-900">
                        {currentDate.format('MMMM YYYY')}
                    </h3>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={goToToday}
                        className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md border"
                    >
                        Today
                    </button>
                    <button
                        onClick={goToPreviousMonth}
                        className="p-1.5 hover:bg-gray-100 rounded-md"
                    >
                        <ChevronLeft className="h-5 w-5 text-gray-600" />
                    </button>
                    <button
                        onClick={goToNextMonth}
                        className="p-1.5 hover:bg-gray-100 rounded-md"
                    >
                        <ChevronRight className="h-5 w-5 text-gray-600" />
                    </button>
                </div>
            </div>

            {/* Calendar Grid */}
            <div className="p-4">
                {/* Weekday Headers */}
                <div className="grid grid-cols-7 gap-2 mb-2">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="text-center text-xs font-semibold text-gray-600 py-2">
                            {day}
                        </div>
                    ))}
                </div>

                {/* Calendar Days */}
                <div className="grid grid-cols-7 gap-2">
                    {calendarDays.map((day, index) => (
                        <div
                            key={index}
                            className={`
                                min-h-[100px] p-2 border rounded-lg
                                ${day.isCurrentMonth ? 'bg-white' : 'bg-gray-50'}
                                ${day.isToday ? 'ring-2 ring-blue-500' : ''}
                            `}
                        >
                            {/* Day Number */}
                            <div className={`
                                text-sm font-semibold mb-1
                                ${day.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}
                                ${day.isToday ? 'text-blue-600' : ''}
                            `}>
                                {day.date.format('D')}
                            </div>

                            {/* Callbacks for this day */}
                            <div className="space-y-1">
                                {day.callbacks.slice(0, 3).map((callback, cbIndex) => (
                                    <button
                                        key={cbIndex}
                                        onClick={() => onCallbackClick(callback)}
                                        className={`
                                            w-full text-left px-2 py-1 rounded text-xs
                                            border transition-all hover:shadow-sm
                                            ${getCallbackColor(callback)}
                                        `}
                                    >
                                        <div className="flex items-center gap-1">
                                            <Clock className="h-3 w-3 flex-shrink-0" />
                                            <span className="font-medium truncate">
                                                {moment(callback.callbackSchedule.scheduledFor).format('h:mm A')}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1 mt-0.5">
                                            <User className="h-3 w-3 flex-shrink-0" />
                                            <span className="truncate">
                                                {callback.firstName} {callback.lastName}
                                            </span>
                                        </div>
                                    </button>
                                ))}
                                {day.callbacks.length > 3 && (
                                    <div className="text-xs text-center text-gray-500 font-medium">
                                        +{day.callbacks.length - 3} more
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Legend */}
            <div className="p-4 border-t bg-gray-50">
                <div className="flex items-center gap-4 text-xs">
                    <span className="font-semibold text-gray-700">Legend:</span>
                    <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded bg-blue-100 border border-blue-300"></div>
                        <span className="text-gray-600">Upcoming</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded bg-orange-100 border border-orange-300"></div>
                        <span className="text-gray-600">Soon (&lt;2h)</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded bg-red-100 border border-red-300"></div>
                        <span className="text-gray-600">Overdue</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded bg-green-100 border border-green-300"></div>
                        <span className="text-gray-600">Completed</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

